import React from "react";
import { useNavigate } from "react-router-dom";
import { UserCog, ArrowLeftCircle } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { toast } from "sonner";

/**
 * Shown at the top of every page when the platform admin has an active
 * "return session" cookie — i.e. they're currently impersonating a demo
 * employee. The one-tap button swaps them back to the demo agency-admin view.
 * Rendered globally by App.js so it's visible from /app, /app/history, etc.
 */
export default function DemoImpersonationBanner() {
  const { user, checkAuth } = useAuth();
  const nav = useNavigate();

  // Show whenever there's a stashed admin session to return to. Works for
  // both a demo agency-admin impersonating an employee AND a platform admin
  // impersonating any demo persona (agency admin or member).
  if (!user || !user.has_return_session) return null;

  const label = user.role === "agency_admin"
    ? `${user.name} — Agency admin view`
    : user.name;

  const goBack = async () => {
    try {
      await api.post("/demo/return");
      // Fetch fresh user shape directly — checkAuth updates state async but
      // doesn't return a value, so we need this to know where to route.
      let returnedUser = null;
      try { returnedUser = (await api.get("/auth/me")).data; } catch { /* noop */ }
      await checkAuth?.();
      const dest = returnedUser?.role === "platform_admin" ? "/platform/personas" : "/agency";
      toast.success("Returned to admin view");
      nav(dest);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not return to admin view");
    }
  };

  return (
    <div data-testid="demo-impersonation-banner"
         className="sticky top-0 z-40 border-b border-amber-500/40 bg-amber-500/10 px-4 py-2 text-amber-600 backdrop-blur">
      <div className="mx-auto flex max-w-7xl items-center justify-between gap-3 text-xs sm:text-sm">
        <div className="flex items-center gap-2">
          <UserCog className="h-4 w-4" />
          <span><strong>Demo mode</strong> — viewing as {label}</span>
        </div>
        <button data-testid="demo-return-button" onClick={goBack}
                className="inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2.5 py-1 font-medium hover:bg-amber-500/20">
          <ArrowLeftCircle className="h-3 w-3" /> Return to admin view
        </button>
      </div>
    </div>
  );
}
